import {
  IconNames,
  IconNamesFilled,
  IconNamesWithIndicator,
  IconRole
} from '../../util/types';
import {
  InfoCircle,
  CheckCircle,
  Warning,
  Error,
  Close,
  Edit,
  Flag,
  Notifications,
  NotificationsWithIndicator,
  QuestionCircle,
  Settings,
  Email,
  Exclude,
  DeleteIcon,
  Dropdown,
  Calendar,
  Bookmark,
  BookmarkWithIndicator,
  Search,
  ResourceCenter,
  Menu,
  Add,
  LinkNewTab,
  ResourceCenterWithIndicator,
  Check,
  LockOpened,
  LockClosed
} from '../../assets/icons';

export interface IconProps extends React.SVGAttributes<HTMLOrSVGImageElement> {
  size: 'small' | 'medium';
  name: IconNames | IconNamesWithIndicator;
  hasIndicator?: boolean;
  className?: string;
  altText?: string;
  role?: IconRole;
  filled?: boolean;
}

const getAllIcons = (
  width?: string,
  height?: string,
  className?: string,
  altText?: string,
  role?: IconRole
): Record<IconNames, JSX.Element> => {
  const classes = `preflight ${className}`;

  return {
    settings: (
      <Settings
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    questionCircle: (
      <QuestionCircle
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    notifications: (
      <Notifications
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    flag: (
      <Flag
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    edit: (
      <Edit
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    infoCircle: (
      <InfoCircle
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    delete: (
      <DeleteIcon
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    resourceCenter: (
      <ResourceCenter
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    search: (
      <Search
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    error: (
      <Error
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    warning: (
      <Warning
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    dropdown: (
      <Dropdown
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    calendar: (
      <Calendar
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    bookmark: (
      <Bookmark
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    menu: (
      <Menu
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    add: (
      <Add
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    exclude: (
      <Exclude
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    email: (
      <Email
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    close: (
      <Close
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    checkCircle: (
      <CheckCircle
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    linkNewTab: (
      <LinkNewTab
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    lockOpened: (
      <LockOpened
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    lockClosed: (
      <LockClosed
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    check: (
      <Check
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    )
  };
};

const getAllIconsWithIndicator = (
  width?: string,
  height?: string,
  className?: string,
  altText?: string,
  role?: string
): Record<IconNamesWithIndicator, JSX.Element> => {
  const classes = `preflight ${className}`;
  return {
    notifications: (
      <NotificationsWithIndicator
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    resourceCenter: (
      <ResourceCenterWithIndicator
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    ),
    bookmark: (
      <BookmarkWithIndicator
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
      />
    )
  };
};

const getAllFilledIcons = (
  width?: string,
  height?: string,
  className?: string,
  altText?: string,
  role?: string,
  filled?: boolean
): Record<IconNamesFilled, JSX.Element> => {
  const classes = `preflight ${className}`;
  return {
    infoCircle: (
      <InfoCircle
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
        filled={filled}
      />
    ),
    error: (
      <Error
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
        filled={filled}
      />
    ),
    warning: (
      <Warning
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
        filled={filled}
      />
    ),
    checkCircle: (
      <CheckCircle
        width={width}
        height={height}
        className={classes}
        title={altText}
        role={role}
        filled={filled}
      />
    )
  };
};

const Icon = ({
  size = 'small',
  name,
  hasIndicator = false,
  className,
  altText,
  role = undefined,
  filled = false
}: IconProps): JSX.Element => {
  const svgSize = {
    small: {
      width: '16',
      height: '16'
    },
    medium: {
      width: '24',
      height: '24'
    }
  };

  const Icons: Record<IconProps['name'], JSX.Element> = getAllIcons(
    svgSize[size].width,
    svgSize[size].height,
    className,
    altText,
    role
  );
  const IconsWithIndicator: Partial<Record<IconProps['name'], JSX.Element>> =
    getAllIconsWithIndicator(
      svgSize[size].width,
      svgSize[size].height,
      className,
      altText,
      role
    );

  const IconsFilled: Partial<Record<IconProps['name'], JSX.Element>> = getAllFilledIcons(
    svgSize[size].width,
    svgSize[size].height,
    className,
    altText,
    role,
    filled
  );
  const iconComponent =
    hasIndicator && !filled
      ? IconsWithIndicator[name] || Icons[name]
      : filled
        ? IconsFilled[name] || Icons[name]
        : Icons[name];
  return <>{iconComponent}</>;
};

export default Icon;

export const AllIcons = Object.keys(getAllIcons()).sort((a, b) =>
  a.localeCompare(b)
) as IconNames[];
export const IconListWithIndicator = Object.keys(getAllIconsWithIndicator()).sort(
  (a, b) => a.localeCompare(b)
) as IconNamesWithIndicator[];
export const IconListFilled = Object.keys(getAllFilledIcons()).sort((a, b) =>
  a.localeCompare(b)
) as IconNamesFilled[];
